/**
 * Browser-side LiveKit session helper.
 *
 * Spins up a `livekit-client` Room, joins it with a token+url pair
 * minted server-side, plays back any remote audio tracks the agent
 * publishes, and exposes the current connection state to the UI.
 *
 * Lifecycle (single source of truth):
 *   1. `connect({ token, url })`  → join + publish mic
 *   2. event handlers attach remote audio elements to the DOM
 *   3. `disconnect()`             → unpublish, leave, dispose
 *
 * We never re-use a Room instance across sessions — each `connect`
 * creates a fresh `Room`. Re-using LK's Room across reconnects is
 * possible but the state machine is finicky and one ghost-track is
 * one too many on a voice product. The cost (a few ms of construction)
 * is negligible.
 */
import * as React from 'react';
import {
  Room,
  RoomEvent,
  Track,
  type RemoteTrack,
  type RemoteParticipant,
  type RemoteTrackPublication,
} from 'livekit-client';

export type LiveKitConnectionState =
  | 'idle'
  | 'connecting'
  | 'connected'
  | 'reconnecting'
  | 'disconnected'
  | 'failed';

export interface LiveKitSession {
  state: LiveKitConnectionState;
  /** Last user-facing error message, if any. Cleared on next `connect`. */
  error: string | null;
  /** True while the LiveKit room is connected and the mic is publishing. */
  isLive: boolean;
  /** Remote participants currently in the room (excludes us). */
  remoteParticipants: ReadonlyArray<{ identity: string; metadata?: string }>;
  connect: (args: { token: string; url: string }) => Promise<void>;
  disconnect: () => Promise<void>;
}

export function useLiveKitSession(): LiveKitSession {
  const [state, setState] = React.useState<LiveKitConnectionState>('idle');
  const [error, setError] = React.useState<string | null>(null);
  const [remotes, setRemotes] = React.useState<
    ReadonlyArray<{ identity: string; metadata?: string }>
  >([]);

  const roomRef = React.useRef<Room | null>(null);
  // We keep attached <audio> elements in a ref so the cleanup path can
  // detach them in O(remotes) without touching DOM IDs.
  const audioElsRef = React.useRef<Map<string, HTMLAudioElement[]>>(new Map());

  const cleanup = React.useCallback(() => {
    for (const els of audioElsRef.current.values()) {
      for (const el of els) {
        try {
          el.pause();
          el.remove();
        } catch {
          /* ignore */
        }
      }
    }
    audioElsRef.current.clear();
    setRemotes([]);
  }, []);

  const disconnect = React.useCallback(async () => {
    const room = roomRef.current;
    if (room) {
      try {
        await room.disconnect();
      } catch {
        /* room may already be disconnected */
      }
    }
    roomRef.current = null;
    cleanup();
    setState('disconnected');
  }, [cleanup]);

  const connect = React.useCallback(
    async ({ token, url }: { token: string; url: string }) => {
      if (roomRef.current) {
        await disconnect();
      }
      setError(null);
      setState('connecting');

      const room = new Room({
        // Adaptive stream + dynacast are wins for video; for an audio
        // bot they're irrelevant but cheap. Enabled to match LK's
        // defaults and stay aligned with their server-side QoS knobs.
        adaptiveStream: true,
        dynacast: true,
      });
      roomRef.current = room;

      const refreshRemotes = () => {
        const list: Array<{ identity: string; metadata?: string }> = [];
        for (const p of room.remoteParticipants.values()) {
          list.push({ identity: p.identity, metadata: p.metadata });
        }
        setRemotes(list);
      };

      room
        .on(RoomEvent.TrackSubscribed, (
          track: RemoteTrack,
          _publication: RemoteTrackPublication,
          participant: RemoteParticipant,
        ) => {
          if (track.kind !== Track.Kind.Audio) return;
          const el = track.attach() as HTMLAudioElement;
          el.autoplay = true;
          // Hidden — the page already has its own UI for the conversation.
          el.style.display = 'none';
          document.body.appendChild(el);
          const list = audioElsRef.current.get(participant.identity) ?? [];
          list.push(el);
          audioElsRef.current.set(participant.identity, list);
        })
        .on(RoomEvent.TrackUnsubscribed, (track) => {
          for (const el of track.detach()) {
            el.remove();
          }
        })
        .on(RoomEvent.ParticipantConnected, refreshRemotes)
        .on(RoomEvent.ParticipantDisconnected, refreshRemotes)
        .on(RoomEvent.Disconnected, () => {
          cleanup();
          setState('disconnected');
        })
        .on(RoomEvent.Reconnecting, () => setState('reconnecting'))
        .on(RoomEvent.Reconnected, () => setState('connected'));

      try {
        await room.connect(url, token);
        await room.localParticipant.setMicrophoneEnabled(true);
        refreshRemotes();
        setState('connected');
      } catch (err) {
        setError((err as Error).message || 'LiveKit connect failed');
        setState('failed');
        cleanup();
        try {
          await room.disconnect();
        } catch {
          /* ignore */
        }
        roomRef.current = null;
        throw err;
      }
    },
    [cleanup, disconnect],
  );

  // Dispose on unmount — never leave the mic LED on when the user
  // navigates away mid-session.
  React.useEffect(() => {
    return () => {
      void disconnect();
    };
  }, [disconnect]);

  return {
    state,
    error,
    isLive: state === 'connected' || state === 'reconnecting',
    remoteParticipants: remotes,
    connect,
    disconnect,
  };
}
